import { makeAutoObservable, runInAction } from 'mobx';
import { consoleAiService, type PlanOutput, type ExecuteOutput } from '../services/console-ai.service';
import { consoleAiActionsService, type ConsoleActionView } from '../services/console-ai-actions.service';
import { consoleAiPreferencesService } from '../services/console-ai-preferences.service';

export interface UserChatMessage { role: 'user'; content: string; }
export interface AssistantTextMessage { role: 'assistant'; kind: 'text'; content: string; }
export interface AssistantToolMessage { role: 'assistant'; kind: 'tool'; actionId: string; summary: string; entityId?: string; }
export interface AssistantErrorMessage { role: 'assistant'; kind: 'error'; message: string; }
export type ChatMessage = UserChatMessage | AssistantTextMessage | AssistantToolMessage | AssistantErrorMessage;

export interface PendingPreview {
  actionId: string;
  params: Record<string, any>;
  summary: string;
  affectedCount?: number;
  warnings?: string[];
  previewToken: string;
  destructive?: boolean;
}

class CdpAiStore {
  sessionId?: string;
  messages: ChatMessage[] = [];
  pendingPreview?: PendingPreview;
  draft: string = '';
  isSending: boolean = false;
  isPanelOpen: boolean = true;

  // Template library state
  actions: ConsoleActionView[] = [];
  actionsLoading: boolean = false;
  actionsLoaded: boolean = false;
  favorites: string[] = [];
  recents: Array<{ actionId: string; lastUsed: string }> = [];
  isLibraryOpen: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  reset(): void {
    this.sessionId = undefined;
    this.messages = [];
    this.pendingPreview = undefined;
    this.draft = '';
    this.isSending = false;
    this.actions = [];
    this.actionsLoading = false;
    this.actionsLoaded = false;
    this.favorites = [];
    this.recents = [];
    this.isLibraryOpen = false;
  }

  setDraft(value: string) { this.draft = value; }
  togglePanel() { this.isPanelOpen = !this.isPanelOpen; }

  openLibrary = () => { this.isLibraryOpen = true; };
  closeLibrary = () => { this.isLibraryOpen = false; };

  loadActions = async (pageContext?: string): Promise<void> => {
    if (this.actionsLoading) return;
    this.actionsLoading = true;
    try {
      const list = await consoleAiActionsService.list(pageContext);
      runInAction(() => {
        this.actions = list;
        this.actionsLoaded = true;
      });
    } finally {
      runInAction(() => { this.actionsLoading = false; });
    }
  };

  loadPreferences = async (): Promise<void> => {
    const p = await consoleAiPreferencesService.get();
    runInAction(() => {
      this.favorites = p.favorites;
      this.recents = p.recents;
    });
  };

  toggleFavorite = async (actionId: string): Promise<void> => {
    const wasFav = this.favorites.includes(actionId);
    runInAction(() => {
      this.favorites = wasFav
        ? this.favorites.filter(x => x !== actionId)
        : [...this.favorites, actionId];
    });
    try {
      if (wasFav) {
        await consoleAiPreferencesService.removeFavorite(actionId);
      } else {
        await consoleAiPreferencesService.addFavorite(actionId);
      }
    } catch (e) {
      // Rollback on failure
      runInAction(() => {
        this.favorites = wasFav
          ? [...this.favorites, actionId]
          : this.favorites.filter(x => x !== actionId);
      });
      throw e;
    }
  };

  async send(message: string, opts: { pageContext?: string; userLanguage: string }): Promise<void> {
    if (!this.sessionId) {
      const s = await consoleAiService.startSession({ pageContextStart: opts.pageContext });
      runInAction(() => { this.sessionId = s.sessionId; });
    }
    runInAction(() => {
      this.messages.push({ role: 'user', content: message });
      this.draft = '';
      this.isSending = true;
    });
    let out: PlanOutput;
    try {
      out = await consoleAiService.postMessage({
        sessionId: this.sessionId!, message, pageContext: opts.pageContext, userLanguage: opts.userLanguage,
      });
    } finally {
      runInAction(() => { this.isSending = false; });
    }
    runInAction(() => {
      if (out.kind === 'text') {
        this.messages.push({ role: 'assistant', kind: 'text', content: out.text });
      } else if (out.kind === 'preview') {
        this.pendingPreview = {
          actionId: out.actionId, params: out.params, summary: out.preview.summary,
          affectedCount: out.preview.affectedCount, warnings: out.preview.warnings,
          previewToken: out.previewToken,
        };
      } else if (out.kind === 'denied') {
        this.messages.push({ role: 'assistant', kind: 'error', message: `Permission denied for ${out.actionId}` });
      } else {
        this.messages.push({ role: 'assistant', kind: 'error', message: out.message });
      }
    });
  }

  async confirm(destructiveTypedName?: string): Promise<void> {
    if (!this.pendingPreview || !this.sessionId) return;
    const preview = this.pendingPreview;
    runInAction(() => { this.isSending = true; });
    let out: ExecuteOutput;
    try {
      out = await consoleAiService.executeAction({
        sessionId: this.sessionId,
        actionId: preview.actionId,
        params: preview.params,
        previewToken: preview.previewToken,
        destructiveTypedName,
      });
    } finally {
      runInAction(() => { this.isSending = false; });
    }
    runInAction(() => {
      this.pendingPreview = undefined;
      if (out.kind === 'result') {
        this.messages.push({
          role: 'assistant', kind: 'tool',
          actionId: preview.actionId, summary: out.summary, entityId: out.entityId,
        });
      } else {
        this.messages.push({
          role: 'assistant', kind: 'error',
          message: out.kind === 'error' ? out.message : out.kind,
        });
      }
    });
  }

  cancel(): void {
    this.pendingPreview = undefined;
  }
}

export const cdpAiStore = new CdpAiStore();
